import { Download, Images } from 'lucide-react';
import { useStore, type InspectorTab } from '@/state/store';
import { TOOLS } from '@/tools/registry';
import { cn } from '@/lib/utils';

/**
 * Phone-only bottom bar. The drawers hide the panel toggles that live in the
 * top bar on desktop, so the primary destinations get thumb-reachable buttons.
 * The middle entries come from the active tool; Photos and Export are fixed at
 * the ends so the bar's shape stays familiar across tools.
 */
export function MobileBar() {
  const tool = useStore((s) => s.tool);
  const leftOpen = useStore((s) => s.ui.leftOpen);
  const rightOpen = useStore((s) => s.ui.rightOpen);
  const tab = useStore((s) => s.ui.tab);
  const setUi = useStore((s) => s.setUi);

  const def = TOOLS[tool];
  const openTab = (next: InspectorTab) =>
    setUi({ tab: next, rightOpen: !(rightOpen && tab === next), leftOpen: false });

  const items = [
    {
      key: 'photos',
      label: def.libraryLabel,
      icon: Images,
      active: leftOpen,
      onClick: () => setUi({ leftOpen: !leftOpen, rightOpen: false }),
    },
    ...def.tabs.map(({ value, label, icon }) => ({
      key: value,
      label,
      icon,
      active: rightOpen && tab === value,
      onClick: () => openTab(value),
    })),
    {
      key: 'export',
      label: 'Export',
      icon: Download,
      active: false,
      onClick: () => setUi({ exportOpen: true }),
    },
  ];

  return (
    <nav className="panel z-30 flex shrink-0 items-stretch justify-around px-1 pb-[env(safe-area-inset-bottom)] hairline-t">
      {items.map(({ key, label, icon: Icon, active, onClick }) => (
        <button
          key={key}
          onClick={onClick}
          aria-pressed={active}
          className={cn(
            'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-[10px] py-1.5',
            'text-[12px] font-medium transition-colors duration-150 active:bg-[var(--bg-hover)]',
            active ? 'text-[var(--accent)]' : 'text-[var(--text-secondary)]',
          )}
        >
          <Icon size={18} strokeWidth={active ? 2.2 : 1.8} />
          {label}
        </button>
      ))}
    </nav>
  );
}
